import {
  Box,
  MenuContent as ChakraMenuContent,
  MenuItem as ChakraMenuItem,
  MenuPositioner,
  MenuRoot,
  MenuTrigger as ChakraMenuTrigger,
  Text,
  type MenuContentProps,
  type MenuItemProps as ChakraMenuItemProps,
  type MenuRootProps,
  type MenuTriggerProps
} from '@chakra-ui/react'
import { forwardRef } from 'react'

const menuContentStyles = {
  minW: '180px',
  borderRadius: '12px',
  boxShadow: 'md',
  borderWidth: '1px',
  borderColor: 'gray.200',
  bg: 'white',
  py: 1
}

const menuItemBaseStyles = {
  _hover: { bg: 'lightGray.200' },
  _focus: { bg: 'lightGray.200' },
  bg: 'transparent',
  fontSize: 'sm',
  fontWeight: 500,
  lineHeight: '18px',
  w: '100%',
  gap: '12px',
  px: '14px',
  py: '11px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer'
} as const

interface MenuProps extends MenuRootProps {
  placement?:
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'top'
    | 'top-start'
    | 'top-end'
}

function Menu({ placement = 'bottom-end', children, ...props }: MenuProps) {
  return (
    <MenuRoot positioning={{ placement }} {...props}>
      {children}
    </MenuRoot>
  )
}

const MenuTrigger = forwardRef<HTMLButtonElement, MenuTriggerProps>(
  ({ children, ...props }, ref) => (
    <ChakraMenuTrigger
      ref={ref}
      cursor="pointer"
      display="flex"
      alignItems="center"
      bg="transparent"
      border="none"
      _focusVisible={{ outline: 'none' }}
      {...props}
    >
      {children}
    </ChakraMenuTrigger>
  )
)

MenuTrigger.displayName = 'MenuTrigger'

const MenuContent = forwardRef<HTMLDivElement, MenuContentProps>(
  ({ children, ...props }, ref) => (
    <MenuPositioner>
      <ChakraMenuContent ref={ref} {...menuContentStyles} {...props}>
        {children}
      </ChakraMenuContent>
    </MenuPositioner>
  )
)

MenuContent.displayName = 'MenuContent'

type MenuItemVariant = 'default' | 'danger'

interface MenuItemProps extends Omit<ChakraMenuItemProps, 'icon'> {
  icon?: React.ReactNode
  variant?: MenuItemVariant
}

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({ icon, variant = 'default', children, onClick, ...props }, ref) => {
    const isDanger = variant === 'danger'
    const textColor = isDanger ? 'danger.500' : 'black.500'
    const iconColor = isDanger ? 'danger.500' : 'black.500'

    return (
      <ChakraMenuItem
        ref={ref}
        {...menuItemBaseStyles}
        onClick={onClick}
        {...props}
      >
        {icon && (
          <Box as="span" color={iconColor} display="inline-flex" flexShrink={0}>
            {icon}
          </Box>
        )}
        <Text fontWeight={isDanger ? 600 : 500} color={textColor}>
          {children}
        </Text>
      </ChakraMenuItem>
    )
  }
)

MenuItem.displayName = 'MenuItem'

export { Menu, MenuContent, MenuItem, MenuTrigger }
